import React, { useState, useEffect } from 'react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "./ui/alert-dialog";

const INITIAL_FORM_STATE = {
  Datecadrage: '',
  B1S1: '',
  B1S2: '',
  B2S1: '',
  B3S1: '',
  B2S2: '',
  B3S2: '',
  Report: ''
};

const BLOC_CONFIG = [
  {
    id: 1,
    title: 'Bloc 1',
    color: 'gray',
    fields: [
      { name: 'B1S1', label: 'Séance 1' },
      { name: 'B1S2', label: 'Séance 2' }
    ]
  },
  {
    id: 2,
    title: 'Bloc 2',
    color: 'gray',
    fields: [
      { name: 'B2S1', label: 'Séance 1' },
      { name: 'B2S2', label: 'Séance 2' }
    ]
  },
  {
    id: 3,
    title: 'Bloc 3',
    color: 'gray',
    fields: [
      { name: 'B3S1', label: 'Séance 1' },
      { name: 'B3S2', label: 'Séance 2' }
    ]
  }
];

const PlanningTab = ({  data, onUpdate, onTabChange, currentTab, requestedTab }) => {
  // États du formulaire
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // États de l'interface
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [error, setError] = useState(null);

  // Formatage des dates
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      console.error('Erreur lors du formatage de la date:', e);
      return '';
    }
  };

  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    date.setHours(date.getHours() - 1);
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Initialisation des données
  useEffect(() => {
    if (data) {
      const formattedData = {
        Datecadrage: formatDateForInput(data.Datecadrage),
        B1S1: formatDateForInput(data.B1S1),
        B1S2: formatDateForInput(data.B1S2),
        B2S1: formatDateForInput(data.B2S1),
        B3S1: formatDateForInput(data.B3S1),
        B2S2: formatDateForInput(data.B2S2),
        B3S2: formatDateForInput(data.B3S2),
        Report: formatDateForInput(data.Report)
      };
      setFormData(formattedData);
      setInitialFormData(formattedData);
    }
  }, [data]);

  // Détection des changements
  useEffect(() => {
    if (initialFormData) {
      const hasChanges = Object.keys(formData).some(
        key => formData[key] !== initialFormData[key]
      );
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, initialFormData]);

  // Gestion des demandes de changement d'onglet
  useEffect(() => {
    if (requestedTab && requestedTab !== currentTab && hasUnsavedChanges) {
      setShowExitDialog(true);
    } else if (requestedTab && !hasUnsavedChanges) {
      onTabChange(requestedTab);
    }
  }, [requestedTab, currentTab, hasUnsavedChanges, onTabChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!hasUnsavedChanges) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      const formattedData = Object.entries(formData).reduce((acc, [key, value]) => {
        acc[key] = formatDateForAPI(value);
        return acc;
      }, {});

      await onUpdate(formattedData);
      setInitialFormData(formData);
      setHasUnsavedChanges(false);

      // Notification de succès
      const notification = document.createElement('div');
      notification.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg transform transition-transform duration-500';
      notification.textContent = 'Planning enregistré avec succès';
      document.body.appendChild(notification);
      setTimeout(() => {
        notification.style.transform = 'translateY(150%)';
        setTimeout(() => document.body.removeChild(notification), 500);
      }, 3000);
    } catch (err) {
      setError('Erreur lors de la mise à jour du planning. Veuillez réessayer.');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmTabChange = async (shouldSave) => {
    try {
      if (shouldSave) {
        await handleSubmit();
      }
      onTabChange(requestedTab);
    } catch (err) {
      setError('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setShowExitDialog(false);
    }
  };

  const DateField = ({ label, name, bgColor = 'gray' }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type="datetime-local"
        name={name}
        value={formData[name]}
        onChange={handleChange}
        className={`
          w-full p-2 border border-gray-300 rounded-md 
          focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          ${bgColor === 'blue' ? 'bg-blue-50/50' : bgColor === 'red' ? 'bg-red-50/50' : 'bg-white'}
        `}
      />
    </div>
  );

  return (
    <>
      <div className="p-6 bg-white rounded-lg shadow">
        {/* Indicateur de modifications */}
        {hasUnsavedChanges && (
          <div className="mb-4 p-2 bg-amber-50 border border-amber-200 text-amber-600 rounded-md flex items-center">
            <span className="mr-2">⚠️</span>
            <span>Modifications non enregistrées</span>
          </div>
        )}

        {/* Message d'erreur */}
        {error && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 text-red-600 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cadrage */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-4">Séance de cadrage</h3>
            <DateField label="Date de cadrage" name="Datecadrage" bgColor="blue" />
          </div>

          {/* Blocs */}
          {BLOC_CONFIG.map(bloc => (
            <div key={bloc.id} className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{bloc.title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bloc.fields.map(field => (
                  <DateField 
                    key={field.name}
                    label={field.label}
                    name={field.name}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Report */}
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-900 mb-4">Report</h3>
            <DateField label="Date de report" name="Report" bgColor="red" />
          </div>

          {/* Bouton de soumission */}
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isSubmitting || !hasUnsavedChanges}
              className={`
                px-6 py-2 rounded-md text-white font-medium
                transition-all duration-200
                ${(isSubmitting || !hasUnsavedChanges)
                  ? 'bg-gray-400 cursor-not-allowed opacity-60'
                  : 'bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow'}
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              `}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Enregistrement en cours...
                </span>
              ) : (
                'Enregistrer le planning'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Dialog de confirmation */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifications non enregistrées</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez des modifications non enregistrées. Voulez-vous les enregistrer avant de quitter cet onglet ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="space-x-2">
            <AlertDialogCancel 
              onClick={() => setShowExitDialog(false)}
              className="hover:bg-gray-100"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleConfirmTabChange(false)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Quitter sans enregistrer
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => handleConfirmTabChange(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Enregistrer et quitter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PlanningTab;